import AbstractResolvableModel from './AbstractResolvableModel';
import { getModelClassResolvableCollection } from './Utils';
import ModelCache, { GlobalCache } from './ModelCache';
import ConsoleGroup from './ConsoleGroup';
import { Collection } from "@mathquis/modelx";
export default class AbstractResolvableCollection extends Collection {
    _resolvingProperties = {};
    _localCache = new ModelCache();
    _subCollections = new Map();
    get cache() {
        return this._localCache;
    }
    listById(identifiers) {
        throw new Error(`${this.constructor.name}.listById is not implemented`);
    }
    async resolveProperty(propName, options) {
        if (this._resolvingProperties[propName]) {
            return;
        }
        this._resolvingProperties[propName] = options;
        const st = Date.now();
        await new Promise((resolve) => {
            setTimeout(resolve, 0);
        });
        const decoratedLogger = AbstractResolvableModel.getDefaults().logger;
        const logger = new ConsoleGroup(decoratedLogger);
        const log = options.log;
        let shouldLog = false;
        const defaultModelClass = options.model;
        const unresolvedModelClass = options.unresolved;
        const cache = !!options.cache;
        const local = !!options.local;
        const cacheHandler = local ? this.cache : GlobalCache;
        const attributeName = options.attributeName;
        if (log) {
            logger.groupStart('<Model>%s</Model> resolving: <Property>%s</Property> <Cache>%s</Cache><Unresolved>%s</Unresolved>', this.model.name, propName, cache ? `cache${local ? ' local' : ''}` : '', unresolvedModelClass ? unresolvedModelClass.name : '');
        }
        const toResolve = this.reduce((toResolve, model) => {
            const attribute = model.get(attributeName);
            const propId = model.getResolvableId(propName);
            const propClass = model.getResolvableModelClass(propName, attributeName, attribute) || defaultModelClass;
            const opts = toResolve.get(propClass) || { models: [], ids: [], cached: {} };
            let cachedModel;
            if (cache && propClass && propId) {
                cachedModel = cacheHandler.get(propClass, propId);
            }
            if (cachedModel) {
                opts.cached['' + propId] = cachedModel;
            }
            else {
                if (propId && opts.ids.indexOf(propId) < 0) {
                    opts.ids.push(propId);
                }
            }
            opts.models.push(model);
            toResolve.set(propClass, opts);
            return toResolve;
        }, new Map());
        let cachedModels = 0;
        let fetchedModels = 0;
        let unresolvedModels = 0;
        let missingModels = 0;
        const promises = Array.from(toResolve.entries()).map(async (entry) => {
            const stm = Date.now();
            const [modelClass, opts] = entry;
            const { ids, cached, models } = opts;
            const cachedResolutions = Array.from(Object.keys(cached)).length;
            let fetchedResolutions = 0;
            let unresolvedResolutions = 0;
            let missingResolutions = 0;
            const groupLog = new ConsoleGroup(decoratedLogger);
            if (log) {
                logger.subGroup(groupLog);
                groupLog.groupStart('Resolving with model <Model>%s</Model>', modelClass.name);
            }
            const propSubCollections = this._subCollections.get(propName) || new Map();
            this._subCollections.set(propName, propSubCollections);
            let col = propSubCollections.get(modelClass);
            if (!col) {
                col = getModelClassResolvableCollection(modelClass);
                propSubCollections.set(modelClass, col);
            }
            if (ids.length > 0) {
                if (log) {
                    groupLog.log('Fetching:', ids);
                }
                await col.listById(ids);
                fetchedResolutions = col.length;
            }
            models.forEach(model => {
                const propId = model.getResolvableId(propName);
                if (propId && (cached['' + propId] || ids.indexOf(propId) >= 0)) {
                    let parent = cached['' + propId];
                    if (!parent) {
                        parent = col.getById(propId);
                    }
                    if (!parent) {
                        shouldLog = true;
                        if (unresolvedModelClass) {
                            unresolvedResolutions++;
                            parent = new unresolvedModelClass({}, { loaded: true });
                            parent.setId(propId);
                            groupLog.warn('Parent <Property>%s</Property> for model <Model>%s</Model> with id <b>%s</b> not found. Using unresolved <Unresolved>%s</Unresolved>', propName, modelClass.name, propId, unresolvedModelClass.name);
                        }
                        else {
                            missingResolutions++;
                            parent = new modelClass({}, { loaded: true });
                            groupLog.error('Parent <Property>%s</Property> for model <Model>%s</Model> with id <b>%s</b> not found. Resorting to default model class. Consider adding "unresolved" option.', propName, modelClass.name, propId);
                        }
                    }
                    model.setResolvable(propName, parent);
                }
            });
            if (log) {
                groupLog.log('Resolved with <Fetch>%s</Fetch><Cache>%s</Cache><Unresolved>%s</Unresolved><Missing>%s</Missing> in<Time>%f</Time>', fetchedResolutions > 0 ? `${fetchedResolutions} fetched` : '', cachedResolutions > 0 ? `${cachedResolutions} cached${local ? ' locally' : ''}` : '', unresolvedResolutions > 0 ? `${unresolvedResolutions} unresolved` : '', missingResolutions > 0 ? `${missingResolutions} missing` : '', Date.now() - stm);
                groupLog.groupEnd();
            }
            cachedModels += cachedResolutions;
            fetchedModels += fetchedResolutions;
            unresolvedModels += unresolvedResolutions;
            missingModels += missingResolutions;
        });
        const results = await Promise.allSettled(promises);
        results.forEach(result => {
            if (result.status === 'rejected') {
                shouldLog = true;
                logger.error(result.reason);
            }
        });
        if (log) {
            logger.log('Resolved <b>%d</b> models with <b>%d</b> resolvables <Fetch>%s</Fetch><Cache>%s</Cache><Unresolved>%s</Unresolved><Missing>%s</Missing> in<Time>%f</Time>', this.length, fetchedModels + cachedModels, fetchedModels > 0 ? `${fetchedModels} fetched` : '', cachedModels > 0 ? `${cachedModels} cached${local ? ' locally' : ''}` : '', unresolvedModels > 0 ? `${unresolvedModels} unresolved` : '', missingModels > 0 ? `${missingModels} missing` : '', Date.now() - st);
        }
        logger.groupEnd();
        if (log || shouldLog) {
            logger.print(false);
        }
        delete this._resolvingProperties[propName];
    }
}
//# sourceMappingURL=AbstractResolvableCollection.js.map